import { formatMoney } from "@bw/shared/utils";
import { Box, Button, Chip, ListItem, Stack, Typography } from "@mui/material";
import type {
  TournamentEventResponse,
  EventEligibilityVerdict,
  TournamentPlayerEntryResponse,
} from "@bw/shared/types/generated";
import { humanizeEntryStatus } from "../utils/entryStatus";
import {
  getTournamentPublicRegistrationPriceState,
  getTournamentRegistrationQuoteState,
} from "../utils/entryFee";
import { PairingInbox } from "./PairingInbox";
import { EventFormatLine } from "./EventFormatLine";
import type { EventCatalogItem } from "../hooks/useTournamentEventCatalog";

/**
 * TournamentEventRow (EPIC 1.2) — the pure, presentational ONE-row-per-event cell
 * of the multi-event register surface (design §2 + §8). Driven ENTIRELY by props
 * (no hooks, no network); the parent (TournamentEventList) owns the data + the
 * register/withdraw dialogs and hands this row three backend facts:
 *   - `event`   — the public catalog row (event_code / name / entry_fee).
 *   - `verdict` — the BACKEND's per-event eligibility and player-specific quote.
 *                 Eligibility is backend-owned; this row renders reasons[0].message
 *                 VERBATIM and NEVER recomputes. A waiver-only blocker may
 *                 open the existing registration dialog to accept that waiver;
 *                 it cannot submit until the backend allows it.
 *   - `myEntry` — the caller's LIVE entry in this event (already resolved by the
 *                 parent via the draw→event join — TournamentPlayerEntryResponse has NO
 *                 event_id). Picks the status/action.
 *
 * The action affordances bubble UP (zero money/eligibility logic here):
 *   - eligible + not-entered (or a withdrawn/rejected/cancelled entry, Bug A) →
 *     an actionable **Register** → onRegister(event).
 *   - an ACTIVE entry (registered / waitlisted / …) → its humanized status + a
 *     **Withdraw** → onWithdraw(entry). The refund POLICY text lives in the
 *     withdraw confirm (the parent), never client money math.
 *   - a MISSING verdict (degraded eligibility fetch) is treated as actionable, not
 *     greyed — an absent fetch must never silently lock the player out (§8).
 *
 * THIN presentation, zero business logic. Signed-in pricing uses the server-owned
 * registration quote; a row without a verdict keeps the public catalog fee display.
 * Missing or inconsistent signed-in quotes render "Fee unavailable". No uuid
 * (event / draw / entry) is ever rendered as visible text (ids ride data-* attrs
 * and the action callbacks, never the DOM text).
 */

/**
 * Entry statuses that mean "NOT actively entered" → the row offers Register again.
 * `withdrawn` is the Bug-A case: a withdrawn entry must read as not-entered so the
 * player can re-register (never a lockout, never a false "You're registered").
 */
const INACTIVE_ENTRY_STATUSES = new Set([
  "withdrawn",
  "rejected",
  "cancelled",
  "declined",
]);

export function TournamentEventRow({
  event,
  verdict,
  myEntry,
  requiresPartner,
  soloDrawId,
  tournamentId,
  registrationOpen = true,
  registrationAccess = "ready",
  onRegister,
  onWithdraw,
  onEnterSolo,
  onSignIn,
  onFindPartner,
  catalog,
}: {
  event: TournamentEventResponse;
  /** The public catalog line for this event (#2691): format, guarantee, entry
   *  rules, spots and waitlist. Display-only; absent when the catalog read failed,
   *  and the row then reads exactly as before. */
  catalog?: EventCatalogItem;
  verdict?: EventEligibilityVerdict;
  myEntry?: TournamentPlayerEntryResponse;
  /**
   * Whether registration is OPEN for this tournament (the page's authoritative
   * lifecycle.registration_state === "open"). Default TRUE so every existing
   * caller/test keeps the exact register affordance. When FALSE, a NOT-entered row
   * renders READ-ONLY (its display facts only — no Register/Enter-solo button),
   * preserving the deleted read-only catalog's browse behavior for a closed/
   * in-progress tournament. An ACTIVE own-entry is UNAFFECTED — a registered player
   * always keeps their status + Withdraw even after registration closes (this
   * mirrors the page's showRegisterList, which stays true while the player holds an
   * entry). DISPLAY-GATE ONLY — it never makes an ineligible row actionable.
   */
  registrationOpen?: boolean;
  /** Registration access is explicit because an absent verdict means different
   * things for a public visitor and a signed-in player whose eligibility read
   * failed. Only `ready` may open the registration dialog. */
  registrationAccess?: "ready" | "sign_in" | "unavailable";
  /** The event's draw.requires_partner (resolved by the parent). A
   *  partner-requiring event routes the not-entered action to "Enter solo"
   *  (you pair later) instead of "Register". */
  requiresPartner?: boolean;
  /** The draw to enter solo (the event's single/primary draw id), passed back
   *  to onEnterSolo verbatim — never a uuid rendered in the DOM. */
  soloDrawId?: string;
  /** The tournament id — only needed so a `partner_wanted` row can mount the
   *  PairingInbox (SLICE 1.4-d), which keys its fold-invalidation off it. Optional
   *  so callers that don't surface the inbox (and the existing row tests) omit it;
   *  the inbox mounts only when present. */
  tournamentId?: string;
  onRegister: (event: TournamentEventResponse) => void;
  onWithdraw: (entry: TournamentPlayerEntryResponse) => void;
  /** Enter a doubles/mixed draw SOLO (SLICE 1.4-a) — pairing deferred. Optional
   *  so callers that never render a partner-requiring row (and the existing
   *  singles tests) need not supply it; the button is wired only when present. */
  onEnterSolo?: (drawId: string) => void;
  onSignIn?: () => void;
  /** Start the partner finder for an own `partner_wanted` entry (SLICE 1.4-c).
   *  Optional for the same backward-compat reason. */
  onFindPartner?: (entry: TournamentPlayerEntryResponse) => void;
}) {
  // An entry is "active" only when it exists AND its status is not one of the
  // not-entered terminals (withdrawn/rejected/cancelled). An active entry drives
  // the status label + Withdraw; an inactive (withdrawn) entry is treated as
  // not-entered → Register returns (Bug A).
  const activeEntry =
    myEntry && !INACTIVE_ENTRY_STATUSES.has(myEntry.status) ? myEntry : null;
  const spotsLeft = catalog?.entry_limit != null ? Math.max(0, catalog.entry_limit - catalog.spots_taken) : null;

  // STATE PRECEDENCE (order matters):
  //   1. An ACTIVE own-entry wins over the verdict. The player's own entry is
  //      EXACTLY what makes the backend verdict say "already entered this
  //      discipline" (the one-per-(mode,gender) cap), so that verdict must NOT
  //      grey the very event the player holds — they must still see Registered/
  //      Waitlisted + Withdraw. (Live-E2E bug: a registered free-singles event was
  //      greyed by its own duplicate_discipline verdict, hiding Withdraw.)
  //   2. Else a PRESENT not-eligible verdict greys the row (+ reasons[0].message),
  //      except a waiver-only blocker: the dialog itself owns the waiver action
  //      and still prevents registration/payment until acceptance is persisted.
  //      An ABSENT verdict (degraded fetch) stays actionable — fail-OPEN on the
  //      read so the player is never silently locked out (the backend re-checks at
  //      register time).
  //   3. Else Register. A withdrawn/rejected entry is NOT active → falls through
  //      here (Re-register if eligible) — the Bug-A re-register path.
  const waiverOnly =
    verdict?.eligible === false &&
    verdict.reasons?.length === 1 &&
    verdict.reasons[0]?.code === "waiver";
  const isIneligible =
    activeEntry == null &&
    verdict != null &&
    verdict.eligible === false &&
    !waiverOnly;
  // The backend's first reason MESSAGE, verbatim — but `reasons` defaults to `[]`
  // in the schema, so {eligible:false, reasons:[]} is reachable. Fall back to
  // honest copy so an ineligible row ALWAYS says something (never a blank greyed
  // row with no reason AND no action — a wedged, unexplained cell).
  const reason = isIneligible
    ? (verdict?.reasons?.[0]?.message ?? "You can't enter this event.")
    : null;

  const feeLabel = formatEntryFee(event, verdict);
  // Merged catalog DISPLAY facts (EPIC2 slice (b)) — ported verbatim from the
  // deleted TournamentEventsSection so the merged row reads byte-identical catalog
  // meta. Both are display-only and gate nothing.
  const formatLine = buildFormatLine(event);
  const countsLine = buildCountsLine(
    event,
    Boolean(requiresPartner) ||
      event.mode === "doubles" ||
      event.mode === "mixed_doubles",
  );

  const metaLine = [formatLine, countsLine].filter(Boolean).join(" · ");
  // #2691 catalog: the server's entry-rules line replaces the derived format line,
  // and a full event reads "Join waitlist" (singles waitlist) or "Full". Solo
  // doubles entry has no waitlist yet, so a full doubles event reads "Full".
  const rulesLine = catalog
    ? [catalog.eligibility_line, catalog.partner_line].filter(Boolean).join(" · ")
    : null;
  const full =
    catalog?.state === "full_closed" ||
    (catalog?.state === "full_waitlist" && Boolean(requiresPartner));
  const joinWaitlist = catalog?.state === "full_waitlist" && !requiresPartner;

  return (
    <ListItem
      divider
      disableGutters
      data-event-id={event.event_id}
      aria-disabled={isIneligible || undefined}
      sx={{
        // Grid: code | details | day + price, with the action under the price.
        // On a phone an active entry's status + Withdraw take their own line
        // under the details, so the right column stays narrow.
        display: "grid",
        gridTemplateColumns: { xs: "40px minmax(0, 1fr) fit-content(40%)", sm: "40px minmax(0, 1fr) auto" },
        gridTemplateAreas: activeEntry
          ? { xs: '"code main side" "code act act"', sm: '"code main side" "code main act"' }
          : '"code main side" "code main act"',
        alignItems: "center",
        bgcolor: isIneligible ? "action.disabledBackground" : undefined,
        columnGap: 1.5,
        rowGap: 0.5,
        px: { xs: 0.5, sm: 1 },
        py: 1.25,
      }}
    >
      <Box
        aria-hidden={event.event_code ? undefined : true}
        data-testid={event.event_code ? "event-code" : undefined}
        sx={{
          gridArea: "code",
          width: 40,
          height: 40,
          borderRadius: "10px",
          bgcolor: "action.hover",
          color: "text.secondary",
          display: "grid",
          placeItems: "center",
          fontWeight: 700,
          // Longer codes ("MD35") shrink to fit rather than being cut.
          fontSize: eventInitials(event).length > 3 ? "0.6875rem" : "0.9375rem",
          letterSpacing: eventInitials(event).length > 3 ? "-0.02em" : undefined,
          overflow: "hidden",
        }}
      >
        {eventInitials(event)}
      </Box>
      <Box sx={{ gridArea: "main", minWidth: 0 }}>
        {/* The name wraps (never truncates the level); the day sits with the price. */}
        <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", overflowWrap: "anywhere" }}>
          {event.name}
        </Typography>
        {/* FORMAT + COUNTS on one line (merged from the read-only catalog): the
            humanized discipline + tier/age range, then the REAL backend draw and
            entry counts. DISPLAY ONLY; they gate nothing. The spans keep their
            test ids; an absent part is omitted, never invented. */}
        {rulesLine ? (
          <Typography variant="body2" color="text.secondary" data-testid="event-rules">
            {rulesLine}
          </Typography>
        ) : catalog ? null : metaLine ? (
          <Typography noWrap variant="body2" color="text.secondary">
            {formatLine ? (
              <span data-testid="event-format">{formatLine}</span>
            ) : null}
            {formatLine && countsLine ? " · " : null}
            {countsLine ? (
              <span data-testid="event-counts">{countsLine}</span>
            ) : null}
          </Typography>
        ) : null}
        {catalog ? <EventFormatLine item={catalog} /> : null}
        {catalog ? (
          <Typography
            variant="body2"
            data-testid="event-spots"
            sx={{ mt: 0.5, color: catalog.state.startsWith("full") ? "error.light" : "text.secondary" }}
          >
            {catalog.state_label}
          </Typography>
        ) : null}
        {/* #2691 first approved, first served: a guardian-held entry holds no
            spot. The sentence sits in the wide column so the right one stays narrow. */}
        {activeEntry?.status === "pending_guardian_consent" ? (
          <Typography variant="body2" color="text.secondary" data-testid="guardian-pending" sx={{ mt: 0.5 }}>
            {guardianPendingLine(spotsLeft)}
          </Typography>
        ) : null}
        {event.grade ? (
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
            {/* SKILL-GRADE BAND — a DISPLAY label only (eligibility is
              tier-governed, so the grade gates nothing). */}
            {event.grade ? (
              <Chip
                size="small"
                color="secondary"
                label={`Grade ${event.grade}`}
                data-testid="event-grade"
              />
            ) : null}
          </Stack>
        ) : null}
      </Box>

      <Stack sx={{ gridArea: "side", alignSelf: activeEntry ? { xs: "start", sm: "end" } : "end", alignItems: "flex-end", gap: 0.5 }}>
      {catalog?.play_day_labels.length ? (
        <Typography color="text.secondary" data-testid="event-days" sx={{ fontSize: "0.8125rem" }}>
          {catalog.play_day_labels.join(" → ")}
        </Typography>
      ) : null}
      <Typography
        variant="body2"
        sx={{ fontWeight: 700 }}
        data-testid="event-fee"
      >
        {feeLabel}
      </Typography>
      </Stack>
      <Box
        data-testid="event-actions"
        sx={{ gridArea: "act", alignSelf: "start", justifySelf: activeEntry ? { xs: "start", sm: "end" } : "end" }}
      >
        {activeEntry ? (
          // (1) An ACTIVE own-entry wins over the verdict → status + Withdraw,
          // even when the verdict says the discipline is capped (it is capped BY
          // this entry). A `partner_wanted` entry additionally surfaces the
          // partner finder (the held-solo state, SLICE 1.4-a).
          <ActiveEntryState
            entry={activeEntry}
            tournamentId={tournamentId}
            onWithdraw={onWithdraw}
            onFindPartner={onFindPartner}
          />
        ) : isIneligible ? (
          // (2) Greyed + the backend's verbatim reason. NO register/Enter-solo
          // affordance (verdict precedence preserved).
          <Typography variant="body2" color="text.secondary">
            {reason}
          </Typography>
        ) : !registrationOpen ? null : full ? (
          <Typography variant="body2" color="text.secondary">
            Full
          </Typography>
        ) : registrationAccess === "sign_in" &&
          onSignIn ? (
          <Button
            type="button"
            size="small"
            variant="contained"
            onClick={onSignIn}
          >
            Sign in to register
          </Button>
        ) : registrationAccess === "unavailable" ? (
          <Typography variant="body2" color="text.secondary">
            Eligibility unavailable
          </Typography>
        ) : requiresPartner && soloDrawId && onEnterSolo ? (
          // (3a) Eligible + not-entered + partner-requiring → ENTER SOLO (SLICE
          // 1.4-a): you enter now and pair later, NOT a plain "Register". The
          // draw id rides the callback, never the DOM text.
          <Button
            type="button"
            size="small"
            variant="contained"
            onClick={() => onEnterSolo(soloDrawId)}
          >
            Enter solo
          </Button>
        ) : (
          // (3b) Eligible + not-entered SINGLES (or a withdrawn/rejected/cancelled
          // entry) → the actionable register affordance. "Re-register" copy when
          // the player previously held a (now-inactive) entry, so a withdrawn row
          // reads as a genuine second attempt rather than a first registration.
          <Button
            type="button"
            size="small"
            variant="contained"
            onClick={() => onRegister(event)}
          >
            {waiverOnly
              ? "Review waiver & register"
              : joinWaitlist
                ? "Join waitlist"
                : myEntry
                  ? "Re-register"
                  : "Enter"}
          </Button>
        )}
      </Box>
    </ListItem>
  );
}

// ---------------------------------------------------------------------------
// The ACTIVE-entry sub-state: a humanized status label (never the raw enum) + a
// Withdraw affordance. A waitlisted hold additionally surfaces its backend
// position (#N), never an invented number.
// ---------------------------------------------------------------------------

/** Never "0 spots left": at zero the line only says what the guardian must do. */
function guardianPendingLine(spotsLeft: number | null): string {
  const ask = "Your guardian needs to approve to secure your spot.";
  return spotsLeft != null && spotsLeft > 0 ? `${spotsLeft} ${spotsLeft === 1 ? "spot" : "spots"} left. ${ask}` : ask;
}

function ActiveEntryState({
  entry,
  tournamentId,
  onWithdraw,
  onFindPartner,
}: {
  entry: TournamentPlayerEntryResponse;
  /** Present only when the parent wants the `partner_wanted` row to surface the
   *  pairing inbox (SLICE 1.4-d). */
  tournamentId?: string;
  onWithdraw: (entry: TournamentPlayerEntryResponse) => void;
  onFindPartner?: (entry: TournamentPlayerEntryResponse) => void;
}) {
  const isWaitlisted = entry.status === "waitlisted";
  // A `partner_wanted` entry is the held-solo state — active (so Withdraw shows)
  // AND awaiting a partner, so it offers the partner finder (SLICE 1.4-a/c) and
  // the incoming-proposal inbox (SLICE 1.4-d).
  const needsPartner = entry.status === "partner_wanted";
  const statusLabel = humanizeEntryStatus(entry.status);

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      sx={{
        alignItems: "center",
        flexWrap: "wrap",
        justifyContent: { xs: "flex-start", sm: "flex-end" },
      }}
    >
      <Chip
        size="small"
        color={isWaitlisted ? "warning" : "primary"}
        role="status"
        label={
          isWaitlisted && entry.waitlist_position != null
            ? `${statusLabel} (#${entry.waitlist_position})`
            : statusLabel
        }
      />
      {needsPartner && onFindPartner ? (
        <Button
          type="button"
          size="small"
          variant="contained"
          onClick={() => onFindPartner(entry)}
        >
          Find a partner
        </Button>
      ) : null}
      <Button
        type="button"
        size="small"
        variant="outlined"
        onClick={() => onWithdraw(entry)}
      >
        Withdraw
      </Button>
      {/* SLICE 1.4-d: incoming-proposal inbox for the held-solo state. A child
          component reading its OWN hook (usePairingsMine) keyed to this entry's
          draw — the row stays presentation-only. Mounts only when the parent
          supplied tournamentId (the fold-invalidation key). */}
      {needsPartner && tournamentId ? (
        <Box sx={{ flexBasis: "100%", mt: 1 }}>
          <PairingInbox drawId={entry.draw_id} tournamentId={tournamentId} />
        </Box>
      ) : null}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Fee — signed-in rows use the same server-owned quote resolver as the dialog.
// Rows without a verdict retain the public catalog fee (normally anonymous, but
// also the established degraded-read state). Registration still fails closed.
// ---------------------------------------------------------------------------

/** Leading tile text: the event code ("MS") or the name's initials. */
function eventInitials(event: TournamentEventResponse): string {
  if (event.event_code) return event.event_code.toUpperCase();
  const words = event.name.replace(/\(.*?\)/g, "").trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
}

/** "$15.00" → "$15"; a price with cents keeps them ("$12.50"). */
function compactMoney(label: string): string {
  return label.replace(/([0-9])\.00(?![0-9])/, "$1");
}

function formatEntryFee(
  event: TournamentEventResponse,
  verdict: EventEligibilityVerdict | undefined,
): string {
  if (verdict === undefined) {
    const publicFee = getTournamentPublicRegistrationPriceState(
      event.public_registration_price,
    );
    if (publicFee.kind === "free") return "Free";
    if (publicFee.kind === "player_specific") return "Sign in for price";
    if (publicFee.kind === "unavailable") return "Fee unavailable";
    return compactMoney(
      formatMoney(
        event.public_registration_price?.amount,
        event.public_registration_price?.currency ?? undefined,
      ),
    );
  }

  const fee = getTournamentRegistrationQuoteState(verdict.registration_quote);
  if (fee.kind === "free") return "Free";
  if (fee.kind === "unavailable") return "Fee unavailable";
  return compactMoney(
    formatMoney(
      verdict.registration_quote?.amount,
      verdict.registration_quote?.currency,
    ),
  );
}

// ---------------------------------------------------------------------------
// Humanized format line — ported VERBATIM from the deleted TournamentEventsSection
// (EPIC2 slice (b) merge). Mode + gender + tier/age range, never a raw enum dump.
// The merged row must read byte-identical catalog meta to the catalog it replaced,
// so these are copied unchanged rather than re-derived.
// ---------------------------------------------------------------------------

const MODE_LABELS: Record<string, string> = {
  singles: "Singles",
  doubles: "Doubles",
  mixed_doubles: "Mixed doubles",
};

const GENDER_LABELS: Record<string, string> = {
  male: "Men's",
  female: "Women's",
  mixed: "Mixed",
  boys: "Boys'",
  girls: "Girls'",
  any: "Open",
};

/**
 * The humanized discipline — "Men's" / "Mixed" / "Singles". The event NAME already
 * states the discipline, so the GENDER leads as the prefix and the MODE word is the
 * fallback only when there is no gender. Either way the raw lowercase enum token is
 * never echoed.
 */
function formatPlayFormat(
  gender: string | null | undefined,
  mode: string | null | undefined,
): string {
  const genderLabel = gender ? GENDER_LABELS[gender] : undefined;
  if (genderLabel) return genderLabel;
  return mode ? (MODE_LABELS[mode] ?? "") : "";
}

/** "Level 1–3" / "Level 1+" / "Level ≤3" — null bounds omitted, never "null". */
function formatTierRange(
  minTier: number | null | undefined,
  maxTier: number | null | undefined,
): string | null {
  if (minTier == null && maxTier == null) return null;
  if (minTier != null && maxTier != null) {
    return minTier === maxTier
      ? `Level ${minTier}`
      : `Level ${minTier}–${maxTier}`;
  }
  if (minTier != null) return `Level ${minTier}+`;
  return `Level ≤${maxTier}`;
}

/** "U18" (max only) / "35+" (min only) / "18–34" (both) — null bounds omitted. */
function formatAgeRange(
  minAge: number | null | undefined,
  maxAge: number | null | undefined,
): string | null {
  if (minAge == null && maxAge == null) return null;
  if (minAge != null && maxAge != null) {
    return minAge === maxAge ? `Age ${minAge}` : `${minAge}–${maxAge}`;
  }
  if (minAge != null) return `${minAge}+`;
  return `U${maxAge}`;
}

function buildFormatLine(event: TournamentEventResponse): string {
  const parts = [
    formatPlayFormat(event.gender, event.mode),
    formatTierRange(event.min_tier, event.max_tier),
    formatAgeRange(event.min_age, event.max_age),
  ].filter((p): p is string => Boolean(p));
  return parts.join(" · ");
}

// ---------------------------------------------------------------------------
// Counts — real backend ints only; an absent count is omitted, never invented.
// Ported VERBATIM from the deleted TournamentEventsSection.
// ---------------------------------------------------------------------------

function buildCountsLine(
  event: TournamentEventResponse,
  requiresPartner = false,
): string | null {
  const parts: string[] = [];
  if (requiresPartner) parts.push("needs partner");
  if (event.draw_count != null) {
    parts.push(`${event.draw_count} draw${event.draw_count === 1 ? "" : "s"}`);
  }
  if (event.entry_count != null) {
    // Partner events count teams; a capped event shows taken/capacity.
    const unit = requiresPartner
      ? "teams"
      : event.entry_count === 1 && event.max_entries == null
        ? "entry"
        : "entries";
    parts.push(
      event.max_entries != null
        ? `${event.entry_count}/${event.max_entries} ${unit}`
        : `${event.entry_count} ${unit}`,
    );
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}
